<template>
    <div>
        <div v-if="template.template_name" class="grid-parent-items-wrapper items-center gap-3 w-full pl-3 px-2">
            <font-awesome-icon icon="grip-vertical" class="text-base-content-300 text-gray-500" />
            
			<div  class="grid-parent-items-inner justify-between transition-all duration-200 py-1 items-center gap-3 rounded-xl cursor-pointer">
				<div class="w-full bg-white rounded-md">
					<div class="w-full">
						<div class="flex w-full gap-3 items-center justify-between text-sm p-2">
							<!-- Input for the label when edited -->
							<div class="flex justify-between w-3/12">
								<span
									v-tippy
									:content="fieldLabel"
									class="section_name bg-transparent w-48 border-0 outline-none p-1 text-md text-base-content font-semibold truncate"
								>
									{{ fieldLabel }}
								</span>
							</div>


							<!-- Expand/Collapse and Remove button -->
							<div class="flex items-center justify-end gap-3 w-3/12">
								<div v-if="isEditPermission">
									<DropdownButton
										:title="$t('pages.workflow_engine.permissions')"
										@click="showGroupsModal"
									/>
								</div>
								<button
                                    v-if="isReadPermission"
									class="cursor-pointer p-3 text-base text-base-content-300 w-6 h-6 flex items-center justify-center rounded-full text-center"
									:class="{
										formListItem__disabled: template.disabled || disabled,
										'hover:bg-primary-focus hover:text-white-text': !isViewMode,
										'opacity-50 cursor-not-allowed': isViewMode,
									}"
									@click="showFile()"
								>
									<!-- @click="removeTemplate(template)" -->
									<font-awesome-icon icon="eye" class="cursor-pointer text-lg" />
								</button>
								<button
                                    v-if="isEditPermission"
									class="cursor-pointer p-3 text-base text-base-content-300 w-6 h-6 flex items-center justify-center rounded-full text-center"
									:class="{
										formListItem__disabled: template.disabled || disabled,
										'hover:bg-primary-focus hover:text-white-text': !isViewMode,
										'opacity-50 cursor-not-allowed': isViewMode,
									}"
									@click="removeTemplate(template)"
								>
									<font-awesome-icon icon="times" class="cursor-pointer text-lg" />
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
        </div>

        <PermissionGroupController
            ref="permissions-controller"
            :disabled="!isEditMode"
            :groups="mappedPermissionGroups"
            :id="template.id"
            :selected="selectedPermissionGroups"
            :loading="checkPermissions.loading"
            @submit="onPermissionGroupsSubmit"
        />
        <FileUrlModal
            :fileUrl="fileUrl"
            :showModal="showModal"
            @close="closeModal"
        />
    </div>
</template>

<script>
import { Dropdown as DropdownButton } from "@shared/components/buttons";
import PermissionGroupController from "@shared/components/PermissionGroupController";
import FileUrlModal from "@shared/file-viewer";
import {
    getTemplatePermissions,
    updateTemplatePermissions,
    getFileUrl
} from "@/modules/packages/services.js";
import {
    getPermissionGroupsIdList,
    mapPermissionGroup
} from "@shared/components/PermissionGroupController/utils";

export default {
    name: "fields",
    props: {
        template: {
            type: Object,
            default: () => { },
        },
        packageId: {
            type: String,
            required: true,
        },
        draggable: {
            type: Boolean,
            default: true
        },
        isEditPermission: {
            type: Boolean,
            default: true
        },
        isReadPermission: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: null,
        },
    },
    components: {
        DropdownButton,
        PermissionGroupController,
        FileUrlModal
    },
    data() {
        return {
            checkPermissions: {
                loading: false,
                data: {
                    client: [],
                    tenant: {},
                },
            },
            fileUrl: "",
            showModal: false
        }
    },
    computed: {
        isEditMode() {
            return this.$route.params.action === 'edit' && !!this.isEditPermission;
        },
        fieldLabel() {
            return this.getFieldLabel(this.template);
        },
        canDeleteField() {
            return !this.template.block_id
        },
        selectedPermissionGroups() {
            return getPermissionGroupsIdList(
                this.mappedPermissionGroups,
                {
                    filter: (el) => !!el.selected,
                    modifier: (el) => el.group_id,
                }
            );
        },
        mappedPermissionGroups() {
            const { tenant, clients } = this.checkPermissions.data;
            const tenantGroups = tenant?.groups || [];
            const clientGroups = clients || [];

                const mappedTenant = mapPermissionGroup(
                    tenantGroups,
                    {
                        modifier: (el) => ({
                            ...el,
                            id: el.group_id,
                        })
                    }
                )
                const mappedClient = mapPermissionGroup(
                    clientGroups,
                    {
                        modifier: (el) => ({
                            ...el,
                            client_id: el.name, // TODO template
                            client_name: el.name,
                        }),
                        listItemModifier: (el) => ({
                            ...el,
                            group_name: el.title,
                        })
                    }
                )
            return {
                tenant: mappedTenant,
                client: mappedClient,
            }
        },
        isViewMode() {
            return this.$route.params.action === 'view' || false;
        },
    },
    methods: {
        getFieldLabel(field) {
            return field.template_name;
        },
        closeModal() {
            this.fileUrl = "";
            this.showModal = false;
        },
        removeTemplate(template) {
            this.$emit('removeTemplate', template);
        },

        async showGroupsModal() {
            // if (!this.isEditMode) return;

            this.checkPermissions.loading = true;
                this.$refs["permissions-controller"]?.showModal();
            try {
                const response = await getTemplatePermissions({
                    templateId: this.template.id,
                    packageId: this.packageId,
                });

                this.checkPermissions.data = response;
            } catch (error) {
                console.error('Failed to fetch template permissions', error);
            }
            this.checkPermissions.loading = false;
        },

        isSelectedModifier(el, selected = this.selectedPermissionGroups) {
            return selected.includes(el.group_id)
        },

        /**
         * @param {String[]} selected selected group IDs
         */
        async onPermissionGroupsSubmit(selected) {
            this.checkPermissions.loading = true;
            try {
                const { tenant, clients } = this.checkPermissions.data;

                let payload = {
                    tenant: {
                        ...tenant,
                        groups: mapPermissionGroup(tenant.groups, {
                            modifier: (el) => ({
                                ...el,
                                selected: this.isSelectedModifier(el, selected),
                            }),
                        })
                    },
                    clients: mapPermissionGroup(clients, {
                        modifier: (el) => ({
                            ...el,
                            selected: this.isSelectedModifier(el, selected),
                        }),
                    })
                }

				payload.clients.forEach(client => {
					const allGroupsSelected = client.groups.every(group => group.selected === true);
					client.selected = allGroupsSelected;
				});

                await updateTemplatePermissions({
                    templateId: this.template.id,
                    packageId: this.packageId,
                    data: payload,
                });

                // update stored data:
                this.checkPermissions.data = payload;
            } catch (error) {
                console.error(this.$t('components.tenant.tenant_packages.file_url_loading_failed'), error);
            }
            this.checkPermissions.loading = false;
            this.$refs["permissions-controller"]?.hideModal();
        },
        async showFile () {
            try {
                const fileUrl = await getFileUrl(this.template.id)
                if (fileUrl && fileUrl !== '') {
                    this.fileUrl = fileUrl;
                    this.showModal = true;
                }
            } catch (error) {
                this.$toast.error(error.response.data.detail || this.$t('components.tenant.tenant_packages.file_url_loading_failed'));
            }
        }
    },
};
</script>

<style lang="scss" scoped>

.block_visible {
    position: relative;
    align-self: center;
    padding-left: 10px;
}
.packageFieldRole{
    margin-left:1.8%;
}
.optionHeading{
    color: #0236bb;
    font-weight: 400;
}
.optionCircle{
    margin-top:5px;
}

.field-tags_container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
}
.block-role-panel-pad {
    padding-left: 18px;
}

.collect-data {
    max-width: 220px;
    min-width: 810px;
    width: 220px;  
    @apply bg-primary-100 border-primary-100;
    @apply h-7;
}

.grid-parent-items-wrapper {
  display: grid;
  grid-template-columns: 16px 1fr;
}

.grid-parent-items-inner {
  display: grid;
  grid-template-columns: 1fr;
}

.grid-child-items-wrapper {
  display: grid;
  grid-template-columns: 100px 1fr 50px;
}
.item {
  cursor: move;
  padding: 10px;
  background: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
}

/* Optional: You can add styling for the ghost class (the element while being dragged) */
.ghost {
  opacity: 0.5;
}
</style>
